import { alpha, Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { type IItemAction, ItemActions } from './ItemAction';

interface ItemProps
{
    percent?: number;
    order?: number;
    children: React.ReactNode;
    leftContent?: React.ReactNode;
    rightContent?: React.ReactNode;
    actions?: IItemAction[];
    onClick?: string | (() => void);
    onDoubleClick?: () => void;
    level?: number;
}

const Item: React.FC<ItemProps> = ({
    percent,
    order,
    children,
    leftContent,
    rightContent,
    actions,
    onClick,
    onDoubleClick,
    level,
}) =>
{
    const theme = useTheme();
    const navigate = useNavigate();

    const innerOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
        {
            if ((e.target as HTMLElement).classList.contains('MuiBackdrop-root'))
            {
                return;
            }
            // Check for clicks on left/right content areas - allow clicks on text
            const target = e.target as HTMLElement;
            if (target.closest('[data-item-content="left"]') || target.closest('[data-item-content="right"]'))
            {
                return;
            }
            // Allow clicks on text area or main container
            if (!target.closest('[data-item-content="text"]') && target !== e.currentTarget)
            {
                return;
            }

            if (onClick)
            {
                if (typeof onClick === 'string')
                {
                    navigate(onClick);
                } else
                {
                    onClick();
                }
            }
        },
        [navigate, onClick],
    );

    // Calculate background color based on percent and level
    const getBackgroundColor = () =>
    {
        if (percent !== undefined && percent < 0)
        {
            // Negative values - red background
            return alpha('#ff8a65', Math.abs(percent) / 100);
        }

        if (level && theme.palette.levelBackgrounds)
        {
            // Use theme level backgrounds for consistent colors
            return theme.palette.levelBackgrounds[level as 1 | 2 | 3 | 4] || alpha(theme.palette.grey[300], 0.5);
        }

        if (percent !== undefined)
        {
            // Fallback: Positive values - primary color background
            const opacity = Math.max(0.1, percent / 100 - 0.2);
            return alpha(theme.palette.primary.main, opacity);
        }

        return alpha(theme.palette.grey[300], 0.5);
    };

    // Get text color based on level
    const getTextColor = () =>
    {
        if (level && level >= 3)
        {
            // Use getContrastText for better readability on dark backgrounds
            return theme.palette.getContrastText(theme.palette.primary.main);
        }

        // Fallback case: use contrast text for grey background
        if (!level && percent === undefined)
        {
            return theme.palette.getContrastText(theme.palette.grey[300]);
        }

        return theme.palette.text.primary;
    };

    const getLevelColor = () =>
    {
        if (level && theme.palette.levelBackgrounds)
        {
            return theme.palette.levelBackgrounds[level as 1 | 2 | 3 | 4];
        }
        return theme.palette.primary.main;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 1.5, md: 2 },
                mt: { xs: 1, sm: 1.25, md: 1.5 },
                mb: { xs: 0.75, sm: 1, md: 1 },
                p: { xs: 1.5, sm: 1.75, md: 2 },
                minHeight: { xs: 56, sm: 60, md: 64, lg: 64 },
                borderRadius: 2,
                border: `1px solid ${alpha(getLevelColor(), 0.2)}`,
                background: theme.palette.background.paper,
                userSelect: 'none',
                flexGrow: 1,
                fontWeight: 400,
                fontSize: '0.9rem',
                transition: 'all 0.2s ease',
                cursor: onClick ? 'pointer' : 'inherit',
                order: order,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: '4px',
                    background: getLevelColor(),
                },
                '&:hover': onClick
                    ? {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(getLevelColor(), 0.3)}`,
                        borderColor: getLevelColor(),
                        bgcolor: getLevelColor(),
                    }
                    : {},
                '& svg': {
                    color: 'inherit',
                },
            }}
            onClick={innerOnClick}
            onDoubleClick={onDoubleClick}
        >
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', width: '100%' }}>
                {leftContent && (
                    <Box data-item-content="left" sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {leftContent}
                    </Box>
                )}
                <Typography data-item-content="text" component="div" sx={{ ml: { xs: 0.75, sm: 1, md: 1.5, lg: 2 }, flexGrow: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    {children}
                </Typography>
            </Box>
            {rightContent && (
                <Box data-item-content="right" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {rightContent}
                    {actions && actions.length !== 0 && <ItemActions actions={actions} />}
                </Box>
            )}
        </Box>
    );
};

export default Item;
